<template>
  <div v-if="forSale" class="py-6">
    <div class="flex items-center">
      <PurchaseAmountDropdown v-model="purchaseAmount" :max="maxQuantity" />
      <form v-if="areOptionsSelected && passesVariationCheck" class="flex grow" @submit.prevent="checkForm">
        <input type="hidden" name="kogus" :value="amount" />
        <input type="hidden" name="id(1)" :value="id" />
        <input v-for="o of optionIds" :key="o" type="hidden" name="option" :value="o" />
        <input type="hidden" name="variation" :value="variationId ?? ''" />
        <Button class="btn-rounded btn-lg ml-2 grow py-2 md:py-2.5" icon-name="cart" is-animated :aria-label="t['w.module.submission_form.add_basket']()">
          {{ t['w.module.submission_form.add_basket']() }}
        </Button>
      </form>
      <InfoTooltip v-else-if="!areOptionsSelected" class="ml-2 grow" theme="error" @click="displayOptionError()">
        <template #content>
          <div class="w-full">
            <Button class="btn-rounded btn-lg w-full py-2 md:py-2.5" disabled :aria-label="t['w.module.submission_form.choose_options']()">
              {{ t['w.module.submission_form.choose_options']() }}
            </Button>
          </div>
        </template>
        <template #default>
          <span class="text-base">{{ t['w.module.submission_form.choose_options_tooltip']() }}</span>
        </template>
      </InfoTooltip>
      <Button v-else class="btn-rounded btn-lg ml-2 grow py-2 md:py-2.5" disabled :aria-label="t['w.module.submission_form.not_available']()" @click="displayOptionError()">
        {{ t['w.module.submission_form.not_available']() }}
      </Button>
      <WishlistButton
        :id="props.id"
        class="btn-medium ml-2 p-2.5"
        :is-added="wishlisted"
        :option-ids="optionIds"
        :variation-id="variationId"
        @on-added-to-wishlist="addToWishlist"
        @on-removed-from-wishlist="wishlisted = false"
      />
    </div>
    <div v-if="props.isError" class="my-2 text-center text-base text-text-attention">{{ t['w.module.submission_form.make_choices']() }}</div>
  </div>
  <template v-else>
    <div class="flex justify-between pb-3 pt-5">
      <div class="flex items-center text-subtitle font-medium">{{ t['w.toode.pole.muugis']() }}</div>
      <WishlistButton
        :id="props.id"
        class="btn-medium"
        :is-added="wishlisted"
        :option-ids="optionIds"
        :variation-id="variationId"
        @on-added-to-wishlist="addToWishlist"
        @on-removed-from-wishlist="wishlisted = false"
      />
    </div>
    <!-- TODO: Develop feature support for "product back in stock" user reminder notification -->
    <!--div class="mt-3 border-t border-border pt-3">
      <div class="flex items-center bg-background-light-strong px-4 py-[18px] text-button-l">
        <Icon class="mr-4 size-5 text-icon-active" name="bulb" />
        {{ t['w.module.submission_form.reminder']() }}
      </div>
      <CheckboxInput v-model="reminderOfStock" class="mt-6 text-base" :label="t['w.module.submission_form.reminder_checkbox']()">{{ t['w.module.submission_form.reminder_checkbox']() }}</CheckboxInput>
      <CheckboxInput v-model="wishForAlternatives" class="mt-4 text-base" :label="t['w.module.submission_form.alternative_checkbox']()">{{ t['w.module.submission_form.alternative_checkbox']() }}</CheckboxInput>
      <Input v-model="reminderEmailAddress" class="mt-7" name="reminderEmail" :label="t['module.email_label']()" hide-label="true" :placeholder="t['w.login.placeholder.email']()" />
      <Button class="btn-rounded mt-4 w-full" icon-name="arrow-right" @click="submitReminder">{{ t['w.module.submission_form.reminder_button']() }}</Button>
    </div-->
  </template>
</template>

<script setup lang="ts">
import PurchaseAmountDropdown from '~/components/product/PurchaseAmountDropdown.vue'
import Button from '~/components/button/Button.vue'
import WishlistButton from '~/components/wishlist-button/WishlistButton.vue'
import { useProductModalStore } from '~/stores/product-modal'
import InfoTooltip from '~/components/info-tooltip/InfoTooltip.vue'
import * as t from '~/messages'
import { useMetaPixel } from '~/composables/meta-pixel'
import { useMiros } from '~/composables/miros'

const { proxy: gtmProxy } = useGtm()

const { proxy: mirosProxy } = useMiros()

const purchaseAmount = computed({
  get() {
    return amount.value
  },
  set(newValue) {
    amount.value = newValue
  },
})

const props = defineProps({
  selectedOptions: { type: Object, default: null },
  selectedVariation: { type: Object, default: null },
  optionGroups: { type: Array, default: null },
  id: { type: Number, default: null },
  url: { type: String, default: null },
  forSale: { type: Boolean, default: false },
  errors: { type: Array, default: () => [] },
  maxQuantity: { type: Number, default: null },
  isError: { type: Boolean, default: false },
  recommendedHalls: { type: Array, default: () => [] },
  // For tracking purposes, clean up later:
  productCode: {
    type: String,
    required: true,
  },
  productName: {
    type: String,
    required: true,
  },
  price: {
    type: String,
    required: true,
  },
  brandName: {
    type: String,
    default: '',
  },
  imageUrl: {
    type: String,
    default: '',
  },
  wishlisted: {
    type: Boolean,
    default: false,
  },
})

/* TODO: Develop feature support for "product back in stock" user reminder notification
const reminderOfStock = ref()
const wishForAlternatives = ref()
const reminderEmailAddress = ref()

const submitReminder = () => {
}
*/

/* TODO: Develop feature support for hall recommendation if product not available
const showIcons = computed(() => {
  return props.recommendedHalls.every((hall) => hall.icon)
})
*/

const productModal = useProductModalStore()

const wishlisted = ref(props.wishlisted)

const optionIds = computed(() => {
  return props.selectedOptions
    ? Object.values(props.selectedOptions)
        .filter((o) => o !== null)
        .map((o) => o.id)
    : null
})

const variationId = computed(() => {
  return props.selectedVariation ? props.selectedVariation.id : null
})

const getAreOptionsSelected = () => {
  return !props.optionGroups || (props.selectedOptions && !Object.values(props.selectedOptions).some((o) => o === null))
}

const metaPixel = useMetaPixel()

const addToWishlist = () => {
  wishlisted.value = true
  sendGtmAddToWishlistEvent()
}

const sendGtmAddToCartEvent = () => {
  gtmProxy.dataLayer.push({
    event: 'addToWishlist',
    ecommerce: {
      currencyCode: useCurrencyCode(),
      add: {
        products: [
          {
            name: props.productName,
            id: props.productCode,
            price: props.price,
            brand: props.brandName,
            category: '',
            // TODO: fix
            //variant: this.selectedOption && this.selectedOption.name || '',
            quantity: amount.value,
          },
        ],
      },
    },
  })
}

const sendGtmAddToWishlistEvent = () => {
  gtmProxy.dataLayer.push({
    event: 'addToWishlist',
    ecommerce: {
      currencyCode: useCurrencyCode(),
      add: {
        products: [
          {
            name: props.productName,
            id: props.productCode,
            price: props.price,
            brand: props.brandName,
          },
        ],
      },
    },
  })
}

const addToCartAnalytics = () => {
  sendGtmAddToCartEvent()
  metaPixel.sendAddToCart({ id: `${props.id}`, quantity: amount.value })
  mirosProxy.externalAddItemToCart({ alias: props.id, price: Number(props.price), quantity: amount.value, currency: 'EUR' })
}

const emit = defineEmits<{
  error: []
  optionsSelected: []
}>()

const displayOptionError = () => {
  emit('error')
}

watch(
  () => getAreOptionsSelected(),
  () => {
    if (getAreOptionsSelected()) {
      emit('optionsSelected')
    }
  }
)

const checkForm = (e) => {
  if (getAreOptionsSelected()) {
    // safety check
    addToCartAnalytics()

    productModal.openModalForProduct(props.id, amount.value, variationId.value, optionIds.value)

    return e.preventDefault()
  }

  displayOptionError()

  e.preventDefault()
}

const noOptionGroupsCreateVariation = () => {
  return props.optionGroups.every((og) => !og.doesCreateVariation)
}

const doesSubmissionPassVariationCheck = () => {
  /*
    checkout must pass one: no option groups, is legacy options, selection has correct matching variation or
    none of the option groups need variation
  */
  return !props.optionGroups || !!props.selectedVariation || noOptionGroupsCreateVariation()
}

const amount = ref(1)
const areOptionsSelected = computed(() => getAreOptionsSelected())
const passesVariationCheck = computed(() => doesSubmissionPassVariationCheck())
</script>
